/**
 * AppState - Zustand store for Lenders App authentication state
 * Based on Members App pattern, simplified for Lenders (no SMS, no registration flow)
 */

import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import ProfileApi from "../api/ProfileApi";
import { ProfileApiResponse } from "../api/Types";

// Organization user data from org_users table
// RBAC Phase 4: Extended with role_code, permissions, max_data_tier from GET /lender/me
export interface OrgUserData {
  org_id: string;
  org_name?: string;
  role: string;
  role_code?: string;         // RBAC Phase 4: e.g. 'FI_ADMIN', 'LOAN_OFFICER'
  org_type?: string;          // RBAC Phase 4: 'FI' or 'INTERNAL'
  first_name: string;
  last_name: string;
  email: string;
  permissions?: string[];     // RBAC Phase 4: Effective permissions from role + overrides
  max_data_tier?: number;     // RBAC Phase 4: Max data tier accessible (1-5)
}

interface AppState {
  isAuthenticated: "yes" | "no" | "maybe";
  profile: ProfileApiResponse | undefined | null;
  orgUser: OrgUserData | undefined | null;
  isLoggedOut: boolean;
  isFetchingProfile: boolean;
  lastSessionCheck: number; // Timestamp of last session verification
  resetState: () => void;
  clearProfileData: () => void;
  fetchProfileData: (forceCheck?: boolean) => Promise<boolean>;
  setProfileData: (newData: ProfileApiResponse) => void;
  clearLogoutFlag: () => void;
}

// 🔒 SECURITY: Development-only authentication bypass
const isDevelopmentMode = import.meta.env.DEV;
const enableDevBypass =
  isDevelopmentMode &&
  (import.meta.env.VITE_DEV_AUTH_BYPASS === true ||
    import.meta.env.VITE_DEV_AUTH_BYPASS === "true");

if (enableDevBypass) {
  if (import.meta.env.DEV) console.log("🔧 DEV MODE: Authentication bypass enabled");
}

// 🔒 SECURITY: Mock profile data for development only
const createMockProfile = (): ProfileApiResponse => ({
  type: "Lender",
  user_id: "dev-lender-123",
  name: "Dev Lender",
  email: "dev-lender@example.com",
  email_verified: true,
  created_at: new Date().toISOString(),
  latest_login: new Date().toISOString(),
  logins_count: 1,
  user_metadata: {
    firstName: "Dev",
    lastName: "Lender",
    initials: "DL",
    organization: "Dev Credit Union",
    role: "Loan Officer",
  },
  app_metadata: {
    registration: {
      is_registered: true,
      email_verified: true,
      date_registration_completed: new Date().toISOString(),
      agreed_to_terms: true,
    },
  },
  registered: true,
  registered_submitted: new Date().toISOString(),
});

const useAppState = create<AppState>()(
  persist(
    (set, get) => ({
      isAuthenticated: "maybe",
      profile: undefined,
      orgUser: undefined,
      isLoggedOut: false,
      isFetchingProfile: false,
      lastSessionCheck: 0,

      resetState: () => {
        set(() => ({
          isAuthenticated: "maybe",
          profile: undefined,
          orgUser: undefined,
          isLoggedOut: false,
          isFetchingProfile: false,
          lastSessionCheck: 0,
        }));
      },

      clearProfileData: () => {
        set(() => ({
          isAuthenticated: "no",
          profile: null,
          orgUser: null,
          isLoggedOut: true,
          isFetchingProfile: false,
          lastSessionCheck: 0,
        }));
      },

      clearLogoutFlag: () => {
        set(() => ({
          isLoggedOut: false,
        }));
      },

      fetchProfileData: async (forceCheck = false) => {
        if (import.meta.env.DEV) console.log("🔍 fetchProfileData: Called", { forceCheck });

        const state = get();
        if (state.isFetchingProfile) {
          if (import.meta.env.DEV) console.log("⏭️ fetchProfileData: Already fetching, skipping");
          return false;
        }

        // Check if we need to verify the session
        // Session check is required if:
        // 1. forceCheck is true (explicit request)
        // 2. No session check has been done yet (lastSessionCheck === 0)
        // 3. Last session check was more than 5 minutes ago (stale check)
        const now = Date.now();
        const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes
        const needsSessionCheck =
          forceCheck ||
          state.lastSessionCheck === 0 ||
          (now - state.lastSessionCheck) > SESSION_CHECK_INTERVAL;

        if (state.isAuthenticated === "yes" && state.profile && !needsSessionCheck) {
          if (import.meta.env.DEV) console.log("✅ fetchProfileData: Already authenticated and session recently verified, skipping");
          return true;
        }

        if (state.isLoggedOut) {
          if (import.meta.env.DEV) console.log("🚫 fetchProfileData: User logged out, not auto-logging in");
          return false;
        }

        if (import.meta.env.DEV) console.log("🔍 fetchProfileData: Verifying session with backend...");
        set(() => ({ isFetchingProfile: true }));

        // 🔒 SECURITY: Development bypass - ONLY active when explicitly enabled
        if (enableDevBypass) {
          if (import.meta.env.DEV) console.log("🔧 DEV MODE: Using mock authentication");
          const mockProfile = createMockProfile();
          set(() => ({
            profile: mockProfile,
            orgUser: {
              org_id: 'dev-org-123',
              org_name: 'Dev Credit Union',
              role: 'admin',
              first_name: 'Dev',
              last_name: 'Lender',
              email: 'dev-lender@example.com',
            },
            isAuthenticated: "yes",
            isFetchingProfile: false,
            lastSessionCheck: Date.now(),
          }));
          return true;
        }

        // 🔒 SECURITY: Production authentication flow
        if (import.meta.env.DEV) console.log("🔍 fetchProfileData: Calling /verascore/profile");
        await ProfileApi(
          async (data) => {
            if (import.meta.env.DEV) console.log("✅ fetchProfileData: Auth0 profile loaded");

            // RBAC Phase 4: Fetch org + role + permissions from /lender/me API
            // This replaces direct supabase.from('org_users') and supabase.from('organizations') queries
            let orgUserData: OrgUserData | null = null;
            try {
              if (import.meta.env.DEV) console.log("🏢 fetchProfileData: Calling GET /lender/me");
              const apiBase = import.meta.env.VITE_API_URL || '/api';
              const meResp = await fetch(`${apiBase}/verascore/lender/me`, {
                method: 'GET',
                headers: {
                  'VS-Client-Id': import.meta.env.VITE_VS_CLIENT_ID,
                  'x-request-purpose': 'lender_review',
                },
                credentials: 'include',
              });

              if (meResp.ok) {
                const meJson = await meResp.json();
                if (meJson.status === 'success' && meJson.data) {
                  const me = meJson.data;
                  if (import.meta.env.DEV) console.log("✅ fetchProfileData: /lender/me resolved", me);
                  orgUserData = {
                    org_id: me.org_id,
                    org_name: me.org_name,
                    role: me.role_code,  // Use RBAC role_code as primary role
                    role_code: me.role_code,
                    org_type: me.org_type,
                    first_name: me.first_name,
                    last_name: me.last_name,
                    email: me.email,
                    permissions: me.permissions || [],
                    max_data_tier: me.max_data_tier || 0,
                  };
                }
              } else {
                if (import.meta.env.DEV) console.warn("⚠️ fetchProfileData: /lender/me failed:", meResp.status);
              }
            } catch (err) {
              if (import.meta.env.DEV) console.error("❌ fetchProfileData: /lender/me error", err);
            }

            set(() => ({
              profile: data,
              orgUser: orgUserData,
              isAuthenticated: "yes",
              isFetchingProfile: false,
              lastSessionCheck: Date.now(), // Record successful session verification
            }));
          },
          (data) => {
            if (import.meta.env.DEV) console.log("❌ fetchProfileData: Failed", data);
            set(() => ({
              profile: null,
              orgUser: null,
              isAuthenticated: "no",
              isFetchingProfile: false,
            }));
          },
          (data) => {
            if (import.meta.env.DEV) console.log("❌ fetchProfileData: Rejected", data);
            set(() => ({
              profile: null,
              orgUser: null,
              isAuthenticated: "no",
              isFetchingProfile: false,
            }));
          }
        );
        return true;
      },

      setProfileData: async (newData: ProfileApiResponse) => {
        if (import.meta.env.DEV) console.log("🔍 setProfileData: Setting profile and fetching org user");

        // First set the profile data
        set(() => ({
          profile: newData,
          isAuthenticated: "yes",
          isFetchingProfile: true,
        }));

        // RBAC Phase 4: Fetch org + role + permissions from /lender/me API
        let orgUserData: OrgUserData | null = null;
        try {
          if (import.meta.env.DEV) console.log("🏢 setProfileData: Calling GET /lender/me");
          const apiBase = import.meta.env.VITE_API_URL || '/api';
          const meResp = await fetch(`${apiBase}/verascore/lender/me`, {
            method: 'GET',
            headers: {
              'VS-Client-Id': import.meta.env.VITE_VS_CLIENT_ID,
              'x-request-purpose': 'lender_review',
            },
            credentials: 'include',
          });

          if (meResp.ok) {
            const meJson = await meResp.json();
            if (meJson.status === 'success' && meJson.data) {
              const me = meJson.data;
              if (import.meta.env.DEV) console.log("✅ setProfileData: /lender/me resolved", me);
              orgUserData = {
                org_id: me.org_id,
                org_name: me.org_name,
                role: me.role_code,
                role_code: me.role_code,
                org_type: me.org_type,
                first_name: me.first_name,
                last_name: me.last_name,
                email: me.email,
                permissions: me.permissions || [],
                max_data_tier: me.max_data_tier || 0,
              };
            }
          } else {
            if (import.meta.env.DEV) console.warn("⚠️ setProfileData: /lender/me failed:", meResp.status);
          }
        } catch (err) {
          if (import.meta.env.DEV) console.error("❌ setProfileData: /lender/me error", err);
        }

        set(() => ({
          orgUser: orgUserData,
          isFetchingProfile: false,
        }));
      },
    }),
    {
      name: "verascore-lender-auth-storage",
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        profile: state.profile,
        orgUser: state.orgUser,
        isLoggedOut: state.isLoggedOut,
        lastSessionCheck: state.lastSessionCheck,
      }),
    }
  )
);

export default useAppState;

